'use client';

import React, { useState, useCallback, useMemo, memo } from 'react';
import Image from 'next/image';
import { normalizeProductImageUrl } from '@/lib/products/product-image';

interface AppImageProps {
  src: string;
  alt: string;
  width?: number;
  height?: number;
  className?: string;
  priority?: boolean;
  quality?: number;
  placeholder?: 'blur' | 'empty';
  blurDataURL?: string;
  fill?: boolean;
  sizes?: string;
  onClick?: () => void;
  onError?: React.ReactEventHandler<HTMLImageElement>;
  onLoad?: React.ReactEventHandler<HTMLImageElement>;
  fallbackSrc?: string;
  loading?: 'lazy' | 'eager';
  unoptimized?: boolean;
  [key: string]: any;
}

const AppImage = memo(function AppImage({
  src,
  alt,
  width,
  height,
  className = '',
  priority = false,
  quality = 85,
  placeholder = 'empty',
  blurDataURL,
  fill = false,
  sizes,
  onClick,
  onError: externalOnError,
  onLoad: externalOnLoad,
  fallbackSrc = '/assets/images/no_image.png',
  loading = 'lazy',
  unoptimized = false,
  ...props
}: AppImageProps) {
  // Ảnh cũ import từ nguồn ngoài còn lưu dạng "//cdn..." hoặc http:// nên phải
  // chuẩn hóa tại đây, nếu không next/image sẽ lỗi và ảnh hiện trống.
  const normalizedSrc = useMemo(
    () => normalizeProductImageUrl(src) || fallbackSrc,
    [src, fallbackSrc]
  );
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // So sánh theo URL thay vì cờ boolean để src mới không bị kẹt ở fallback.
  const hasError = failedSrc === normalizedSrc;
  const imageSrc = hasError ? fallbackSrc : normalizedSrc;

  const isExternalUrl = useMemo(() => imageSrc.startsWith('http'), [imageSrc]);
  const resolvedUnoptimized = unoptimized || isExternalUrl;

  const handleError = useCallback(
    (event: React.SyntheticEvent<HTMLImageElement>) => {
      if (normalizedSrc !== fallbackSrc) {
        setFailedSrc(normalizedSrc);
      }
      setIsLoading(false);
      externalOnError?.(event);
    },
    [normalizedSrc, fallbackSrc, externalOnError]
  );

  const handleLoad = useCallback(
    (event: React.SyntheticEvent<HTMLImageElement>) => {
      setIsLoading(false);
      externalOnLoad?.(event);
    },
    [externalOnLoad]
  );

  const imageClassName = useMemo(() => {
    const classes = [className];
    if (isLoading) classes.push('bg-gray-200');
    if (onClick) classes.push('cursor-pointer hover:opacity-90 transition-opacity duration-200');
    return classes.filter(Boolean).join(' ');
  }, [className, isLoading, onClick]);

  const imageProps = useMemo(() => {
    const baseProps: any = {
      src: imageSrc,
      alt,
      className: imageClassName,
      quality,
      placeholder,
      unoptimized: resolvedUnoptimized,
      onError: handleError,
      onLoad: handleLoad,
      onClick,
    };

    if (priority) {
      baseProps.priority = true;
    } else {
      baseProps.loading = loading;
    }

    if (blurDataURL && placeholder === 'blur') {
      baseProps.blurDataURL = blurDataURL;
    }

    return baseProps;
  }, [
    imageSrc,
    alt,
    imageClassName,
    quality,
    placeholder,
    blurDataURL,
    resolvedUnoptimized,
    priority,
    loading,
    handleError,
    handleLoad,
    onClick,
  ]);

  if (fill) {
    return (
      <div className="relative" style={{ width: '100%', height: '100%' }}>
        <Image
          {...imageProps}
          fill
          sizes={sizes || '(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw'}
          {...props}
        />
      </div>
    );
  }

  return (
    <Image {...imageProps} width={width || 400} height={height || 300} sizes={sizes} {...props} />
  );
});

AppImage.displayName = 'AppImage';

export default AppImage;
